import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { searchPcg, type PcgAccount } from '../lib/pcg2026';
import { useTvaRates } from '../hooks/useTvaRates';

// --- Types ---

type Step = 'upload' | 'review' | 'confirm' | 'success';

interface ParsedTransaction {
  id: string;
  date: string;
  libelle: string;
  montant_centimes: number;
  type: 'recette' | 'depense';
  suggested_compte: string | null;
  suggested_compte_libelle: string | null;
  confidence: number;
}

interface ReviewRow {
  id: string;
  selected: boolean;
  date: string;
  libelle: string;
  montant_centimes: number;
  type: 'recette' | 'depense';
  compte_principal: string;
  client_id: string | null;
  fournisseur_id: string | null;
  confidence: number;
  is_duplicate: boolean;
  is_out_of_period: boolean;
  tva_taux: number; // TVA rate percentage (e.g. 20 for 20%)
}



interface ReleveParseResult {
  bank_name: string;
  period: { from: string; to: string } | null;
  transactions: ParsedTransaction[];
  raw_text: string;
}

interface ImportHistory {
  id: string;
  filename: string;
  transaction_count: number;
  status: string;
  imported_at: string;
}

interface Props {
  entrepriseId: string;
  exerciceId: string;
}

const DRAFT_KEY = 'import_releve_draft';

function formatCentimes(centimes: number): string {
  const abs = Math.abs(centimes);
  const euros = (abs / 100).toFixed(2);
  return centimes < 0 ? `-${euros}` : euros;
}


export default function ImportReleve({ entrepriseId, exerciceId }: Props) {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { rates: tvaRates } = useTvaRates();

  const [step, setStep] = useState<Step>('upload');
  const [parseMode, setParseMode] = useState<'pdfreader' | 'rawpdf'>('pdfreader');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bankName, setBankName] = useState<string | null>(null);
  const [period, setPeriod] = useState<{ from: string; to: string } | null>(null);
  const [rows, setRows] = useState<ReviewRow[]>([]);
  const [filter, setFilter] = useState<'all' | 'recette' | 'depense' | 'uncat'>('all');
  const [importResult, setImportResult] = useState<{ imported: number; skipped_duplicates: number } | null>(null);
  const [history, setHistory] = useState<ImportHistory[]>([]);
  const [activePcgDropdown, setActivePcgDropdown] = useState<string | null>(null);
  const [pcgSearches, setPcgSearches] = useState<Record<string, string>>({});
  const [pcgOptions, setPcgOptions] = useState<Record<string, PcgAccount[]>>({});
  const blurTimer = useRef<NodeJS.Timeout | null>(null);
  const [bulkPcgSearch, setBulkPcgSearch] = useState('');
  const [bulkPcgOptions, setBulkPcgOptions] = useState<{ numero: string; libelle: string }[]>([]);

  // Load draft from localStorage
  useEffect(() => {
    const draft = localStorage.getItem(DRAFT_KEY);
    if (draft) {
      try {
        const parsed = JSON.parse(draft);
        if (parsed.rows?.length > 0) {
          setRows(parsed.rows);
          setBankName(parsed.bankName);
          setPeriod(parsed.period);
          if (parsed.pcgSearches) setPcgSearches(parsed.pcgSearches);
          setStep('review');
        }
      } catch {}
    }
  }, []);

  // Load import history
  useEffect(() => {
    api.importReleve.history(entrepriseId).then(setHistory).catch(() => {});
  }, [entrepriseId]);

  useEffect(() => {
    if (!bulkPcgSearch || bulkPcgSearch.length < 2) { setBulkPcgOptions([]); return; }
    const timeout = setTimeout(async () => {
      try {
        const data = await api.planComptes.list({ search: bulkPcgSearch });
        setBulkPcgOptions(data.slice(0, 10));
      } catch { /* ignore */ }
    }, 300);
    return () => clearTimeout(timeout);
  }, [bulkPcgSearch]);

  const handlePcgSearch = (rowId: string, term: string) => {
    setPcgSearches((prev) => ({ ...prev, [rowId]: term }));
    if (term.length < 2) {
      setPcgOptions((prev) => ({ ...prev, [rowId]: [] }));
      return;
    }
    setPcgOptions((prev) => ({ ...prev, [rowId]: searchPcg(term).slice(0, 30) }));
  };

  // Save draft on row changes
  useEffect(() => {
    if (rows.length > 0) {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ rows, bankName, period, pcgSearches }));
    }
  }, [rows, bankName, period, pcgSearches]);

  const handleFileSelect = useCallback(async (file: File) => {
    setLoading(true);
    setError(null);
    try {
      const result: ReleveParseResult = await api.importReleve.parse(entrepriseId, exerciceId, file, parseMode);
      setBankName(result.bank_name);
      setPeriod(result.period);

      const reviewRows: ReviewRow[] = result.transactions.map((tx) => {
        const suggested = tx.suggested_compte || '';
        return {
          id: tx.id,
          selected: true,
          date: tx.date,
          libelle: tx.libelle,
          montant_centimes: tx.montant_centimes,
          type: tx.type,
          compte_principal: suggested,
          client_id: null,
          fournisseur_id: null,
          confidence: tx.confidence,
          is_duplicate: false,
          is_out_of_period: false,
          tva_taux: 20, // Default 20% TVA
        };
      });

      // Real-time duplicate check against DB
      const dupePayload = reviewRows.map(r => ({
        date: r.date,
        ttc_centimes: Math.abs(r.montant_centimes),
        libelle: r.libelle,
      }));
      try {
        const { duplicates } = await api.importReleve.checkDuplicates(entrepriseId, dupePayload);
        for (const idx of duplicates) {
          if (reviewRows[idx]) reviewRows[idx].is_duplicate = true;
        }
      } catch { /* non-blocking */ }

      setRows(reviewRows);
      setStep('review');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'analyse du PDF');
    } finally {
      setLoading(false);
    }
  }, [entrepriseId, exerciceId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const toggleRow = (id: string) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id && !r.is_out_of_period ? { ...r, selected: !r.selected } : r))
    );
  };

  const toggleAll = () => {
    const allSelected = rows.filter((r) => !r.is_out_of_period).every((r) => r.selected);
    setRows((prev) =>
      prev.map((r) => (r.is_out_of_period ? r : { ...r, selected: !allSelected }))
    );
  };

  const updateRow = (id: string, updates: Partial<ReviewRow>) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const updated = { ...r, ...updates };
        return updated;
      })
    );
  };

  const selectedRows = rows.filter((r) => r.selected);
  const filteredRows = rows.filter((r) => {
    if (filter === 'recette') return r.type === 'recette';
    if (filter === 'depense') return r.type === 'depense';
    if (filter === 'uncat') return !r.compte_principal;
    return true;
  });

  const totalRecettes = rows.filter((r) => r.selected && r.type === 'recette').reduce((s, r) => s + Math.abs(r.montant_centimes), 0);
  const totalDepenses = rows.filter((r) => r.selected && r.type === 'depense').reduce((s, r) => s + Math.abs(r.montant_centimes), 0);

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        entreprise_id: entrepriseId,
        exercice_id: exerciceId,
        transactions: selectedRows.map((r) => ({
          date: r.date,
          libelle: r.libelle,
          montant_centimes: r.montant_centimes,
          type: r.type,
          compte_principal: r.compte_principal,
          client_id: r.client_id,
          fournisseur_id: r.fournisseur_id,
          tva_taux: r.tva_taux,
        })),
      };
      const result = await api.importReleve.confirm(payload);
      setImportResult(result);
      setStep('success');
      localStorage.removeItem(DRAFT_KEY);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'import');
    } finally {
      setLoading(false);
    }
  };

  // --- Step 1: Upload ---
  if (step === 'upload') {
    return (
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Import relevé bancaire</h1>

        {/* Parse mode selector */}
        <div className="mb-4 flex gap-3">
          {(['pdfreader', 'rawpdf'] as const).map((m) => (
            <button
              key={m}
              onClick={() => setParseMode(m)}
              className={`flex-1 px-4 py-3 rounded-xl border-2 text-left transition-colors ${
                parseMode === m ? 'border-amber-400 bg-amber-50' : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <p className="font-semibold text-sm text-gray-800">{m}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {m === 'pdfreader'
                  ? 'PDF → pdfreader → suppression en-tête → LLM'
                  : 'PDF brut → LLM directement'}
              </p>
            </button>
          ))}
        </div>

        <div
          className="bg-white rounded-2xl shadow-sm border-2 border-dashed border-amber-300 p-12 text-center cursor-pointer hover:border-amber-400 transition-colors"
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          {loading ? (
            <div>
              <div className="animate-spin w-8 h-8 border-4 border-amber-400 border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-gray-600">Analyse du relevé en cours...</p>
            </div>
          ) : (
            <>
              <p className="text-lg font-medium text-gray-700 mb-2">
                Glissez votre relevé bancaire PDF ici
              </p>
              <p className="text-gray-500 mb-1">ou cliquez pour sélectionner un fichier</p>
              <p className="text-sm text-gray-400">PDF uniquement · Max 20 Mo</p>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            onChange={handleFileChange}
            className="hidden"
            data-testid="pdf-file-input"
          />
        </div>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Import history */}
        {history.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Historique des imports</h2>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 divide-y divide-gray-100">
              {history.map((h) => (
                <div key={h.id} className="px-4 py-3 flex items-center justify-between text-sm">
                  <span className="text-gray-700">{h.filename}</span>
                  <span className="text-gray-500">{new Date(h.imported_at).toLocaleDateString('fr-FR')}</span>
                  <span className="text-gray-500">{h.transaction_count} écritures</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${h.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {h.status === 'completed' ? 'OK' : 'Erreur'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- Step 2: Review ---
  if (step === 'review') {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Import relevé — {rows.length} transactions
            </h1>
            {bankName && period && (
              <p className="text-sm text-gray-500 mt-1">
                {bankName} — {period.from} au {period.to}
              </p>
            )}
          </div>
        </div>

        {/* Filter pills */}
        <div className="flex gap-2 mb-4">
          {(['all', 'recette', 'depense', 'uncat'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 text-sm rounded-full transition-colors ${
                filter === f
                  ? 'bg-amber-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f === 'all' ? 'Tous' : f === 'recette' ? 'Recettes' : f === 'depense' ? 'Dépenses' : 'Non catégorisés'}
            </button>
          ))}
        </div>

        {/* Bulk PCG assignment bar */}
        {rows.filter(r => r.selected).length > 0 && (
          <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-4">
            <span className="text-sm font-medium text-blue-700">
              {rows.filter(r => r.selected).length} ligne(s) sélectionnée(s)
            </span>
            <input
              type="text"
              placeholder="Rechercher un compte PCG..."
              className="border border-gray-300 rounded px-3 py-1.5 text-sm w-48"
              value={bulkPcgSearch}
              onChange={(e) => setBulkPcgSearch(e.target.value)}
            />
            {bulkPcgOptions.length > 0 && (
              <select
                className="border border-gray-300 rounded px-3 py-1.5 text-sm"
                onChange={(e) => {
                  const compte = e.target.value;
                  if (!compte) return;
                  setRows(prev => prev.map(r =>
                    r.selected ? { ...r, compte_principal: compte } : r
                  ));
                  setBulkPcgSearch('');
                  setBulkPcgOptions([]);
                }}
              >
                <option value="">Assigner...</option>
                {bulkPcgOptions.map(o => (
                  <option key={o.numero} value={o.numero}>{o.numero} — {o.libelle}</option>
                ))}
              </select>
            )}
            <button
              onClick={() => setRows(prev => prev.map(r => ({ ...r, selected: false })))}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Désélectionner
            </button>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={rows.filter((r) => !r.is_out_of_period).every((r) => r.selected)}
                    onChange={toggleAll}
                  />
                </th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Libellé</th>
                <th className="px-3 py-2 text-right">Montant</th>
                <th className="px-3 py-2">TVA %</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Compte PCG</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredRows.map((row) => (
                <tr
                  key={row.id}
                  className={`${
                    row.is_out_of_period
                      ? 'bg-red-50'
                      : row.is_duplicate
                      ? 'bg-yellow-50'
                      : row.confidence < 50
                      ? 'border-l-4 border-l-orange-400'
                      : ''
                  }`}
                >
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={row.selected}
                      disabled={row.is_out_of_period}
                      onChange={() => toggleRow(row.id)}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="date"
                      value={row.date}
                      onChange={(e) => updateRow(row.id, { date: e.target.value })}
                      className="px-2 py-1 border border-gray-300 rounded text-sm"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={row.libelle}
                        onChange={(e) => updateRow(row.id, { libelle: e.target.value })}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                      {row.confidence < 50 && (
                        <span
                          data-testid={`low-confidence-${row.id}`}
                          className="text-orange-500 text-xs whitespace-nowrap"
                          title={`Confiance: ${row.confidence}%`}
                        >
                          ⚠ {row.confidence}%
                        </span>
                      )}
                      {row.is_duplicate && (
                        <span className="bg-yellow-200 text-yellow-800 text-xs px-1.5 py-0.5 rounded whitespace-nowrap">
                          Doublon
                        </span>
                      )}
                      {row.is_out_of_period && (
                        <span className="bg-red-200 text-red-800 text-xs px-1.5 py-0.5 rounded whitespace-nowrap">
                          Hors exercice
                        </span>
                      )}
                    </div>
                  </td>
                  <td className={`px-3 py-2 text-right font-mono ${row.type === 'recette' ? 'text-green-700' : 'text-red-700'}`}>
                    {formatCentimes(Math.abs(row.montant_centimes))} €
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={row.tva_taux}
                      onChange={(e) => updateRow(row.id, { tva_taux: parseFloat(e.target.value) })}
                      className="px-1 py-1 border border-gray-300 rounded text-sm"
                    >
                      {tvaRates.map((r) => (
                        <option key={r} value={r}>{r}%</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => updateRow(row.id, { type: row.type === 'recette' ? 'depense' : 'recette' })}
                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                        row.type === 'recette' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {row.type === 'recette' ? 'Recette' : 'Dépense'}
                    </button>
                  </td>
                  <td className="px-3 py-2 relative min-w-[220px] max-w-[280px]">
                    <div className="relative">
                      <input
                        type="text"
                        value={pcgSearches[row.id] ?? row.compte_principal}
                        onFocus={() => {
                          if (blurTimer.current) clearTimeout(blurTimer.current);
                          setActivePcgDropdown(row.id);
                        }}
                        onBlur={() => {
                          blurTimer.current = setTimeout(() => setActivePcgDropdown(null), 200);
                        }}
                        onChange={(e) => {
                          const val = e.target.value;
                          handlePcgSearch(row.id, val);
                          if (!val.trim()) {
                            updateRow(row.id, { compte_principal: '' });
                          }
                        }}
                        placeholder="Nº ou libellé…"
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                      {activePcgDropdown === row.id && (pcgOptions[row.id]?.length ?? 0) > 0 && (
                        <ul className="absolute z-20 mt-1 left-0 w-72 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {pcgOptions[row.id]?.map((acc) => (
                            <li
                              key={acc.numero}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                updateRow(row.id, { compte_principal: acc.numero });
                                setPcgSearches((prev) => ({ ...prev, [row.id]: `${acc.numero} — ${acc.libelle}` }));
                                setActivePcgDropdown(null);
                              }}
                              className="px-3 py-2 text-sm hover:bg-amber-50 cursor-pointer border-b border-gray-50 last:border-0 whitespace-nowrap"
                            >
                              <strong>{acc.numero}</strong> — {acc.libelle}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            {rows.filter((r) => r.selected && r.type === 'recette').length} recettes ({formatCentimes(totalRecettes)} €) ·{' '}
            {rows.filter((r) => r.selected && r.type === 'depense').length} dépenses ({formatCentimes(totalDepenses)} €) ·{' '}
            {selectedRows.length} sélectionnées
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => {
                if (confirm('Annuler l\'import en cours ? Les données saisies seront perdues.')) {
                  setStep('upload');
                  setRows([]);
                  setBankName(null);
                  setPeriod(null);
                  localStorage.removeItem(DRAFT_KEY);
                }
              }}
              className="px-4 py-2 text-gray-500 hover:text-gray-700 text-sm transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={() => setStep('confirm')}
              disabled={selectedRows.length === 0 || selectedRows.some((r) => !r.compte_principal)}
              title={selectedRows.some((r) => !r.compte_principal) ? 'Toutes les lignes doivent avoir un Compte PCG' : ''}
              className="px-6 py-2 bg-amber-500 text-white font-medium rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Continuer →
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            {error}
          </div>
        )}
      </div>
    );
  }

  // --- Step 3: Confirm ---
  if (step === 'confirm') {
    return (
      <div className="max-w-xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Confirmer l'import</h1>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <p className="text-lg font-semibold text-gray-900 mb-4">
            {selectedRows.length} écritures à importer
          </p>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Recettes</span>
              <p className="text-green-700 font-mono font-medium">+{formatCentimes(totalRecettes)} €</p>
            </div>
            <div>
              <span className="text-gray-500">Dépenses</span>
              <p className="text-red-700 font-mono font-medium">-{formatCentimes(totalDepenses)} €</p>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => setStep('review')}
            className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors"
          >
            ← Retour
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="flex-1 px-4 py-3 bg-amber-500 text-white font-medium rounded-xl hover:bg-amber-600 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Import en cours...' : `Importer ${selectedRows.length} écritures`}
          </button>
        </div>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            {error}
          </div>
        )}
      </div>
    );
  }

  // --- Step 4: Success ---
  return (
    <div className="max-w-xl mx-auto text-center">
      <div className="text-6xl mb-4">✓</div>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">
        {importResult?.imported} écritures importées
      </h1>
      {importResult?.skipped_duplicates ? (
        <p className="text-sm text-yellow-600 mb-6">
          {importResult.skipped_duplicates} doublons ignorés
        </p>
      ) : null}

      <button
        onClick={() => navigate('/transactions')}
        className="px-6 py-3 bg-amber-500 text-white font-medium rounded-xl hover:bg-amber-600 transition-colors"
      >
        Voir les transactions →
      </button>
    </div>
  );
}
